import type { Ref } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from '@eti/providers';
import { css, etiColors, resetButtonStyle } from '@eti/styles';
import { price, usePrevious } from '@eti/utils';
import { m as motion } from 'framer-motion';
import CartNotification from './MobileCartNotification';

const fixedPositionStyles = css`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  z-index: 12;
`;

const buttonStyles = (showFooter: boolean) => css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  align-items: center;
  background-color: ${etiColors.black};
  color: ${etiColors.white};
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  transform: translateY(${showFooter ? 0 : '100%'});
  transition: transform 500ms cubic-bezier(0.75, 0, 0.175, 1);
  width: 100%;
`;

const summaryStyles = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
`;

const labelStyles = css`
  font-size: 0.75rem;
`;

const totalPriceStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;

const linkStyles = css`
  color: ${etiColors.white};
  font-size: 0.75rem;
  text-decoration: underline; /* stylelint-disable-next-line rule-empty-line-before */
  &:hover,
  &:focus-visible {
    color: ${etiColors.white};
  }
`;

const usePositionedDebugLink = (footerHeight: number) => {
  useEffect(() => {
    const debugLink: HTMLAnchorElement | null = document.querySelector('#debug-link');

    if (debugLink) {
      debugLink.style.bottom = `${footerHeight}px`;
    }
    return () => {
      if (debugLink) {
        debugLink.style.bottom = '0';
      }
    };
  }, [footerHeight]);
};

const useHideFooterIfInputIsFocused = () => {
  const [showFooter, setShowFooter] = useState(true);

  const hideFixedFooterOnInputFocus = ({ target }: FocusEvent) => {
    if (
      target instanceof HTMLInputElement &&
      ['text', 'email', 'tel'].some((type) => type === target.type)
    ) {
      setShowFooter(false);
    }
  };

  const showFixedFooterOnBlur = () => setShowFooter(true);

  useEffect(() => {
    document.addEventListener('blur', showFixedFooterOnBlur, true);
    document.addEventListener('focus', hideFixedFooterOnInputFocus, true);
    return () => {
      document.removeEventListener('blur', showFixedFooterOnBlur);
      document.removeEventListener('focus', hideFixedFooterOnInputFocus);
    };
  }, []);

  return { showFooter };
};

export interface FixedFooterProps {
  addedProductsNumber: number;
  footerHeight: number;
  footerRef: Ref<HTMLButtonElement>;
  isCartOpen: boolean;
  onClick: () => void;
  totalPrice: number;
}

const FixedFooter = ({
  addedProductsNumber,
  footerHeight,
  footerRef,
  isCartOpen,
  onClick,
  totalPrice,
}: FixedFooterProps) => {
  const { t } = useTranslation();

  usePositionedDebugLink(footerHeight);
  const { showFooter } = useHideFooterIfInputIsFocused();
  const previousTotalPrice = usePrevious(totalPrice) ?? 0;
  const initialPricePosition = previousTotalPrice < totalPrice ? 20 : -20;

  return (
    <div className={fixedPositionStyles}>
      {showFooter && (
        <CartNotification
          addedProductsNumber={addedProductsNumber}
          footerHeight={footerHeight}
          onClick={onClick}
        />
      )}
      <button
        className={buttonStyles(showFooter)}
        data-testid="mobile-cart-toggle-button"
        onClick={onClick}
        ref={footerRef}
        type="button"
      >
        <div className={summaryStyles}>
          <div className={labelStyles}>{t('Cart.TotalPrice.Label')}</div>
          <span
            style={{
              display: 'inline-block',
              overflow: 'hidden',
            }}
          >
            <motion.div
              animate={{ y: 0 }}
              className={totalPriceStyles}
              data-testid="mobile-cart-totalPrice"
              initial={{ y: initialPricePosition }}
              key={totalPrice}
              transition={{ duration: 0.8 }}
            >
              {price.formatPrice(totalPrice)}
            </motion.div>
          </span>
        </div>
        <span className={linkStyles}>
          {isCartOpen ? t('Cart.HideDetails.Label') : t('Cart.ShowDetails.Label')}
        </span>
      </button>
    </div>
  );
};

export default FixedFooter;
